/**
 * RegisterModal.js
 * Modal dialog to register an RFID card UID with a name + student ID.
 * Opens when user clicks "Register" on an unknown card OR
 * manually from the "Register Card" button (with empty UID field).
 */

import React, { useState, useEffect, useRef } from "react";

export default function RegisterModal({ uid, onSave, onClose }) {
  const [name,      setName]      = useState("");
  const [studentId, setStudentId] = useState("");
  const [uidInput,  setUidInput]  = useState(uid || "");
  const [error,     setError]     = useState("");
  const nameRef = useRef(null);

  // Focus name field on open
  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  // Keep UID field in sync if parent changes it
  useEffect(() => {
    setUidInput(uid || "");
  }, [uid]);

  function handleSubmit(e) {
    e.preventDefault();
    const trimUID = uidInput.trim().toUpperCase();
    if (!trimUID)             { setError("UID is required.");        return; }
    if (!name.trim())         { setError("Full name is required.");  return; }
    if (!studentId.trim())    { setError("Student ID is required."); return; }
    setError("");
    onSave(trimUID, name, studentId);
  }

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    /* ── Backdrop ────────────────────────────────────────────── */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4
                 bg-slate-900/30 backdrop-blur-sm animate-fade-in"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* ── Card ───────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md
                      border border-slate-200 animate-slide-in">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5
                        border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-brand-50 flex items-center
                            justify-center text-brand-600">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800">Register Card</h3>
              <p className="text-xs text-slate-400">Link RFID UID to a person</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center
                       text-slate-400 hover:bg-slate-100 transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">

          {/* UID field */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">
              Card UID
            </label>
            <input
              type="text"
              value={uidInput}
              onChange={(e) => setUidInput(e.target.value.toUpperCase())}
              placeholder="e.g. A1 B2 C3 D4"
              readOnly={!!uid}           /* lock if pre-filled from a scan */
              className={`w-full px-4 py-2.5 rounded-xl border text-sm font-mono
                          font-semibold tracking-widest focus:outline-none
                          focus:ring-2 focus:ring-brand-300 transition-all
                          ${uid
                            ? "bg-slate-50 text-slate-500 cursor-not-allowed border-slate-200"
                            : "bg-white text-slate-800 border-slate-300"
                          }`}
            />
            {uid && (
              <p className="text-xs text-slate-400 mt-1">
                UID pre-filled from the scanned card.
              </p>
            )}
          </div>

          {/* Full name */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">
              Full Name <span className="text-red-400">*</span>
            </label>
            <input
              ref={nameRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. John Smith"
              className="w-full px-4 py-2.5 rounded-xl border border-slate-300
                         text-sm focus:outline-none focus:ring-2 focus:ring-brand-300
                         text-slate-800 placeholder-slate-400 transition-all bg-white"
            />
          </div>

          {/* Student / Employee ID */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">
              Student / Employee ID <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
              placeholder="e.g. STU-2024-001"
              className="w-full px-4 py-2.5 rounded-xl border border-slate-300
                         text-sm focus:outline-none focus:ring-2 focus:ring-brand-300
                         text-slate-800 placeholder-slate-400 transition-all bg-white"
            />
          </div>

          {/* Error */}
          {error && (
            <p className="text-xs text-red-500 font-medium bg-red-50 border
                          border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="btn-ghost flex-1">
              Cancel
            </button>
            <button type="submit" className="btn-primary flex-1 justify-center">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Register
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
